
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import Navbar from "../components/Homepage/Navbar/Navbar";
import Footer from "../components/Homepage/Footer/Footer";

import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";

import {
  EventAvailable,
  EventBusy,
  EventNote,
  AddCircleOutline,
  History,
  Logout,
  LockReset,
  Visibility,
  VisibilityOff,
  Close,
} from "@mui/icons-material";

// Local backend
// const API_URL = "http://127.0.0.1:5000";

// Production backend, when deployed:
const API_URL = "https://rukap.edu.in/leave-api";

const NAVY = "#0B3C5D";

export default function EmployeeDashboard() {
  const navigate = useNavigate();

  // Employee dashboard state
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Change password state
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrentPassword, setShowCurrentPassword] =
    useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Load employee profile
  useEffect(() => {
    const token = sessionStorage.getItem("leaveToken");
    let user = null;

    try {
      user = JSON.parse(
        sessionStorage.getItem("leaveUser") || "null"
      );
    } catch {
      sessionStorage.removeItem("leaveUser");
    }

    if (!token || !user || user.role !== "EMPLOYEE") {
      navigate("/leave-portal", { replace: true });
      return;
    }

    let cancelled = false;

    const loadEmployee = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/employee/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!cancelled) {
          setEmployee(response.data.employee || response.data);
        }
      } catch (err) {
        if (cancelled) return;

        if (
          err.response?.status === 401 ||
          err.response?.status === 403
        ) {
          sessionStorage.removeItem("leaveToken");
          sessionStorage.removeItem("leaveUser");
          navigate("/leave-portal", { replace: true });
          return;
        }

        setError(
          err.response?.data?.message ||
            "Unable to load employee details. Please try again."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEmployee();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Logout
  const handleLogout = () => {
    sessionStorage.removeItem("leaveToken");
    sessionStorage.removeItem("leaveUser");
    navigate("/leave-portal");
  };

  // Open change password dialog
  const openPasswordDialog = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMessage("");
    setPasswordError("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setPasswordDialog(true);
  };

  // Close dialog
  const closePasswordDialog = () => {
    if (passwordLoading) return;

    setPasswordDialog(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMessage("");
    setPasswordError("");
  };

  // Change employee password
  const handleChangePassword = async (event) => {
    event.preventDefault();

    setPasswordMessage("");
    setPasswordError("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all password fields.");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(
        "New password must contain at least 8 characters."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(
        "New password and confirmation do not match."
      );
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError(
        "Your new password must differ from your current password."
      );
      return;
    }

    const token = sessionStorage.getItem("leaveToken");

    if (!token) {
      setPasswordError(
        "Your session has expired. Please log in again."
      );
      return;
    }

    try {
      setPasswordLoading(true);

      const response = await axios.put(
        `${API_URL}/api/employee/change-password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPasswordMessage(
        response.data?.message || "Password changed successfully."
      );

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (err) {
      setPasswordError(
        err.response?.data?.message ||
          "Unable to change password. Please try again."
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  // Leave summary calculations
  const entitlement = Number(
    employee?.annual_leave_entitlement ?? 0
  );

  const taken = Number(employee?.leave_taken ?? 0);
  const remaining = entitlement - taken;

  const summaryCards = [
    {
      title: "Annual Leave",
      value: entitlement,
      icon: <EventAvailable />,
    },
    {
      title: "Leave Taken",
      value: taken,
      icon: <EventBusy />,
    },
    {
      title: "Available Leave",
      value: remaining,
      icon: <EventNote />,
    },
  ];

  // Employee profile information
  const employeeDetails = [
    {
      label: "Employee Name",
      value: employee?.employee_name,
    },
    {
      label: "Employee Code",
      value: employee?.employee_code,
    },
    {
      label: "Designation",
      value: employee?.designation,
    },
    {
      label: "Employee Type",
      value: employee?.employee_type,
    },
    {
      label: "Department",
      value: employee?.department_name,
    },
    {
      label: "College",
      value: employee?.college_name,
    },
    {
      label: "Email Address",
      value: employee?.email,
    },
    {
      label: "Mobile Number",
      value: employee?.mobile,
    },
    {
      label: "Leave Year",
      value: employee?.leave_year || new Date().getFullYear(),
    },
  ];

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "80vh",
          backgroundColor: "#F7F8FA",
          py: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="lg">
          {/* Dashboard heading */}
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 2.5,
              borderRadius: 1,
              borderTop: `4px solid ${NAVY}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    color: NAVY,
                    fontWeight: 600,
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  }}
                >
                  Employee Leave Dashboard
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Rayalaseema University, Kurnool
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
              >
                <Button
                  variant="outlined"
                  startIcon={<LockReset />}
                  onClick={openPasswordDialog}
                  sx={{
                    borderColor: NAVY,
                    color: NAVY,
                    textTransform: "none",
                  }}
                >
                  Change Password
                </Button>

                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<Logout />}
                  onClick={handleLogout}
                  sx={{
                    borderColor: "#B8C1CA",
                    textTransform: "none",
                  }}
                >
                  Logout
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Employee loading state */}
          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress size={32} />
              <Typography
                sx={{ mt: 2 }}
                color="text.secondary"
              >
                Loading employee details...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : employee ? (
            <>
              {/* Leave services */}
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  mb: 2.5,
                  borderRadius: 1,
                  borderTop: `3px solid ${NAVY}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: NAVY,
                    fontWeight: 600,
                    fontSize: "1.1rem",
                  }}
                >
                  Leave Services
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, mb: 2 }}
                >
                  Apply for leave or view your previous applications.
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddCircleOutline />}
                    onClick={() =>
                      navigate("/leave-portal/apply")
                    }
                    sx={{
                      py: 1.1,
                      backgroundColor: NAVY,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#082D46",
                      },
                    }}
                  >
                    Apply for Leave
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<History />}
                    onClick={() =>
                      navigate("/leave-portal/my-applications")
                    }
                    sx={{
                      py: 1.1,
                      borderColor: NAVY,
                      color: NAVY,
                      textTransform: "none",
                    }}
                  >
                    My Leave Applications
                  </Button>
                </Stack>
              </Paper>

              {/* Employee profile */}
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  mb: 2.5,
                  borderRadius: 1,
                  backgroundColor: "#FFFFFF",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: NAVY,
                    fontWeight: 600,
                    fontSize: "1.1rem",
                  }}
                >
                  Employee Profile
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, mb: 2 }}
                >
                  Official employee information
                </Typography>

                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  {employeeDetails.map((detail) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={detail.label}
                    >
                      <Box
                        sx={{
                          minWidth: 0,
                          pb: 1.5,
                          borderBottom: "1px solid #E5E7EB",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {detail.label}
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: "#222B35",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {detail.value !== null &&
                          detail.value !== undefined &&
                          detail.value !== ""
                            ? String(detail.value)
                            : "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {/* Leave summary */}
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: NAVY,
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    mb: 0.5,
                  }}
                >
                  Leave Summary
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Leave balance for{" "}
                  {employee.leave_year || new Date().getFullYear()}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  {summaryCards.map((card) => (
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      key={card.title}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: "100%",
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#EEF3F7",
                            color: NAVY,
                            borderRadius: 1,
                          }}
                        >
                          {card.icon}
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {card.title}
                          </Typography>

                          <Typography
                            variant="h5"
                            sx={{
                              mt: 0.5,
                              fontWeight: 600,
                              color: NAVY,
                            }}
                          >
                            {card.value}
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              sx={{ ml: 0.75 }}
                            >
                              days
                            </Typography>
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </>
          ) : (
            <Alert severity="warning">
              No employee details were returned.
            </Alert>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              textAlign: "center",
              mt: 3,
            }}
          >
            Rayalaseema University | Employee Leave Management System
          </Typography>
        </Container>
      </Box>

      {/* Change Password Dialog */}
      <Dialog
        open={passwordDialog}
        onClose={closePasswordDialog}
        fullWidth
        maxWidth="sm"
      >
        <Box
          component="form"
          onSubmit={handleChangePassword}
        >
          <DialogTitle
            sx={{
              color: NAVY,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <LockReset />
              <span>Change Password</span>
            </Stack>

            <IconButton
              aria-label="Close"
              onClick={closePasswordDialog}
              disabled={passwordLoading}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Enter your current password and choose a new password
              for your employee account.
            </Typography>

            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}

            {passwordMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {passwordMessage}
              </Alert>
            )}

            <Stack spacing={2}>
              <TextField
                fullWidth
                required
                label="Current Password"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                autoComplete="current-password"
                onChange={(e) =>
                  setCurrentPassword(e.target.value)
                }
                disabled={passwordLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Toggle current password visibility"
                        onClick={() =>
                          setShowCurrentPassword((previous) => !previous)
                        }
                        edge="end"
                      >
                        {showCurrentPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                required
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                autoComplete="new-password"
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={passwordLoading}
                inputProps={{ minLength: 8 }}
                helperText="Use at least 8 characters."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Toggle new password visibility"
                        onClick={() =>
                          setShowNewPassword((previous) => !previous)
                        }
                        edge="end"
                      >
                        {showNewPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                required
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                autoComplete="new-password"
                onChange={(e) =>
                  setConfirmPassword(e.target.value)
                }
                disabled={passwordLoading}
                inputProps={{ minLength: 8 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Toggle confirmation password visibility"
                        onClick={() =>
                          setShowConfirmPassword((previous) => !previous)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              type="button"
              onClick={closePasswordDialog}
              disabled={passwordLoading}
              color="inherit"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={passwordLoading}
              startIcon={
                passwordLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <LockReset />
                )
              }
              sx={{
                backgroundColor: NAVY,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#082D46",
                },
              }}
            >
              {passwordLoading ? "Updating..." : "Update Password"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Footer />
    </>
  );
}
